// EntryTypeIcon.js
// One icon vocabulary for the artifacts a Function produces. The composite
// result tabs, the Output block's entry checklist and the View builder's palette
// all label the same four entry kinds, and each of them used to carry its own
// private icon map — so the same table could appear as three different pictures
// depending on which screen the user was looking at.
//
// KIND FIRST: `data`, `chart` and `folder` are what they are whatever they are
// called, so the kind alone decides. Only `file` is refined further, because "a
// file" is not a useful picture when the user is hunting for the spreadsheet
// among six PDFs. That refinement reads the extension, then the MIME type, and
// both are hints rather than guarantees — an unrecognised file always falls back
// to the generic sheet of paper rather than guessing wrong.

import React from 'react';
import BarChartIcon from '@mui/icons-material/BarChart';
import DataObjectIcon from '@mui/icons-material/DataObject';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import GridOnIcon from '@mui/icons-material/GridOn';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';

const EXTENSION_ICONS = {
  xlsx: GridOnIcon,
  xls: GridOnIcon,
  csv: GridOnIcon,
  pdf: PictureAsPdfIcon,
  docx: DescriptionIcon,
  doc: DescriptionIcon,
  json: DataObjectIcon,
};

// Ordered because a MIME type can satisfy more than one pattern: the Office
// spreadsheet type contains both "spreadsheet" and "officedocument".
const MIME_ICONS = [
  [/spreadsheet|ms-excel|csv/i, GridOnIcon],
  [/pdf/i, PictureAsPdfIcon],
  [/msword|wordprocessing/i, DescriptionIcon],
  [/json/i, DataObjectIcon],
];

const fileExtension = (filename) => {
  const name = String(filename || '');
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const fileIconFor = (filename, mime) => {
  const byExtension = EXTENSION_ICONS[fileExtension(filename)];
  if (byExtension) return byExtension;
  const byMime = MIME_ICONS.find(([pattern]) => pattern.test(String(mime || '')));
  return byMime ? byMime[1] : InsertDriveFileIcon;
};

// The icon COMPONENT for an entry, for the rare caller that needs the type
// rather than an element (a map lookup replaced by one shared rule).
export const entryTypeIconFor = (kind, filename = null, mime = null) => {
  if (kind === 'data') return TableChartIcon;
  if (kind === 'chart') return BarChartIcon;
  if (kind === 'folder') return FolderIcon;
  return fileIconFor(filename, mime);
};

function EntryTypeIcon({ kind, filename = null, mime = null, fontSize = 'small', ...iconProps }) {
  const Icon = entryTypeIconFor(kind, filename, mime);
  return <Icon fontSize={fontSize} {...iconProps} />;
}

export default EntryTypeIcon;
